import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useLanguageMutations } from '~/hooks/use-languages';
import { languageSchema, EMPTY_LANGUAGE, type LanguageFormValues } from '~/lib/schemas/language';

interface LanguageEditDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The language being edited, or null/undefined when adding a new one. */
	initial: LanguageFormValues | null;
}

/** Add / edit a language. RHF + zod, mirroring the Promotion/Product editor save flow. */
export function LanguageEditDialog({ open, onOpenChange, initial }: LanguageEditDialogProps) {
	const { t } = useTranslation();
	const { create, update } = useLanguageMutations();
	const form = useForm<LanguageFormValues>({
		resolver: zodResolver(languageSchema) as Resolver<LanguageFormValues>,
		defaultValues: initial ?? EMPTY_LANGUAGE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	// Re-seed the form whenever the dialog opens for a different row.
	useEffect(() => {
		if (open) reset(initial ?? EMPTY_LANGUAGE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.languages.editTitle') : t('system.languages.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.languages.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.languages.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('system.languages.columns.localName')} error={formState.errors.ln?.message}>
						<Input {...register('ln')} aria-invalid={Boolean(formState.errors.ln)} />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.languages.columns.iso2')} error={formState.errors.iso2?.message}>
							<Input {...register('iso2')} maxLength={2} aria-invalid={Boolean(formState.errors.iso2)} />
						</FormField>
						<FormField label={t('system.languages.columns.culture')} error={formState.errors.cc?.message}>
							<Input {...register('cc')} maxLength={6} aria-invalid={Boolean(formState.errors.cc)} />
						</FormField>
					</div>
					<FormField label={t('system.languages.columns.sort')} error={formState.errors.si?.message}>
						<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="lang-visible">{t('system.languages.columns.visible')}</Label>
						<Switch id="lang-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
